'use client'

import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TocEntry, TocTreeNode } from '@/utilities/extractTocFromLexical'
import { TOC_HEADER_OFFSET_PX } from '@/utilities/tocConstants'
import { cn } from '@/utilities/ui'

const IO_THRESHOLDS = [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 1] as const

/** Scrollbar fino — usado só no mobile. */
export const TOC_SCROLLBAR_CLASSES =
  'overflow-y-auto overscroll-y-contain [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.28)_transparent] dark:[scrollbar-color:rgba(255,255,255,0.22)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/25 dark:[&::-webkit-scrollbar-thumb]:bg-foreground/30'

/** @deprecated mantido para compatibilidade de importação. */
export const TOC_SCROLL_AREA_MIN_HEIGHT_CLASS = ''

/** Rótulo uppercase acima da lista. */
export function TocSemrushHeading({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <h2
      className={cn(
        'text-muted-foreground mb-0 text-xs font-semibold uppercase tracking-wide',
        className,
      )}
    >
      {children}
    </h2>
  )
}

/** Scroll suave com compensação do header fixo. */
export function scrollToHeading(id: string) {
  const el = document.getElementById(id)
  if (!el || typeof window === 'undefined') return
  const top = el.getBoundingClientRect().top + window.scrollY - TOC_HEADER_OFFSET_PX
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  try {
    window.history.replaceState(null, '', `#${encodeURIComponent(id)}`)
  } catch {
    window.history.replaceState(null, '', `#${id}`)
  }
}

function computeActiveHeadingId(entries: readonly { id: string }[]): string | null {
  if (!entries.length || typeof document === 'undefined') return null
  const offset = TOC_HEADER_OFFSET_PX + 8
  let current: string | null = null
  for (const { id } of entries) {
    const el = document.getElementById(id)
    if (!el) continue
    if (el.getBoundingClientRect().top <= offset) current = id
    else break
  }
  if (current) return current
  const docEl = document.documentElement
  const maxScroll = docEl.scrollHeight - window.innerHeight
  const nearBottom =
    maxScroll <= 0 ||
    window.scrollY >= maxScroll - 4 ||
    window.scrollY + window.innerHeight >= docEl.scrollHeight - 8
  if (nearBottom) {
    for (let i = entries.length - 1; i >= 0; i--) {
      if (document.getElementById(entries[i]!.id)) return entries[i]!.id
    }
  }
  return entries[0]?.id ?? null
}

export function useActiveTocId(entries: readonly TocEntry[], enabled: boolean) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const entriesRef = useRef(entries)
  entriesRef.current = entries
  const idsKey = useMemo(() => entries.map((e) => e.id).join('\u001f'), [entries])
  const runUpdate = useCallback(() => {
    setActiveId(computeActiveHeadingId(entriesRef.current))
  }, [])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !entries.length) {
      setActiveId(null)
      return
    }
    const headings = entries
      .map((e) => document.getElementById(e.id))
      .filter((el): el is HTMLElement => Boolean(el))
    if (!headings.length) {
      setActiveId(null)
      return
    }
    runUpdate()
    const observer = new IntersectionObserver(() => runUpdate(), {
      root: null,
      rootMargin: `-${Math.max(0, TOC_HEADER_OFFSET_PX - 1)}px 0px -42% 0px`,
      threshold: [...IO_THRESHOLDS],
    })
    headings.forEach((h) => observer.observe(h))
    return () => observer.disconnect()
  }, [enabled, idsKey, runUpdate, entries.length])

  return activeId
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Verifica se activeId pertence ao node ou a qualquer descendente. */
function isDescendantActive(node: TocTreeNode, activeId: string | null): boolean {
  if (!activeId) return false
  if (node.id === activeId) return true
  return node.children.some((child) => isDescendantActive(child, activeId))
}

// ── Componentes ───────────────────────────────────────────────────────────────

/** @deprecated mantido para compatibilidade de tipos */
export type TocListDensity = 'default' | 'sidebar'

type TocNestedListProps = {
  nodes: readonly TocTreeNode[]
  activeId: string | null
  depth: number
  onActivate: (id: string) => void
  /** @deprecated ignorado */
  density?: TocListDensity
}

/**
 * Lista TOC com:
 * - Indicador de posição via borda esquerda colorida (rail estilo Stripe/Vercel)
 * - H3s fechados por defeito, abrem automaticamente quando o leitor chega à secção H2
 * - Animação CSS grid (sem JS para medir altura)
 * - H4+ visíveis enquanto o H3 pai estiver aberto
 */
export function TocNestedList({ nodes, activeId, depth, onActivate }: TocNestedListProps) {
  const t = useTranslations('Blog')
  if (nodes.length === 0) return null

  return (
    <ul className="m-0 list-none p-0">
      {nodes.map((node) => {
        const isActive = node.id === activeId
        const hasChildren = node.children.length > 0
        // Secção ativa = este node ou qualquer descendente é o heading atual
        const sectionOpen = depth === 0 && hasChildren && isDescendantActive(node, activeId)

        return (
          <li key={node.id} className="m-0 p-0">
            {/* Link com rail esquerdo */}
            <a
              href={`#${node.id}`}
              aria-current={isActive ? 'true' : undefined}
              title={t('goTo', { text: node.text })}
              className={cn(
                'block border-l-2 leading-snug transition-colors duration-150 ease-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                depth === 0 && 'py-[5px] pl-3 text-[13.5px] font-medium',
                depth === 1 && 'py-1 pl-6 text-[12.5px] font-normal',
                depth >= 2 && 'py-[3px] pl-9 text-[12px] font-normal',
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
              )}
              onClick={(e) => {
                e.preventDefault()
                onActivate(node.id)
              }}
            >
              {node.text}
            </a>

            {/*
             * H3s (filhos de H2, depth=0): accordion scroll-driven.
             * Abre quando o leitor está na secção, fecha quando sai.
             * Usa CSS grid-template-rows para animar altura sem JS.
             */}
            {depth === 0 && hasChildren && (
              <div
                aria-hidden={!sectionOpen}
                className={cn(
                  'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
                  sectionOpen
                    ? 'grid-rows-[1fr] opacity-100'
                    : 'grid-rows-[0fr] opacity-0',
                )}
              >
                <div className="min-h-0">
                  <TocNestedList
                    nodes={node.children}
                    activeId={activeId}
                    depth={1}
                    onActivate={onActivate}
                  />
                </div>
              </div>
            )}

            {/* H4+ dentro de H3 aberto: sempre visíveis */}
            {depth >= 1 && hasChildren && (
              <TocNestedList
                nodes={node.children}
                activeId={activeId}
                depth={depth + 1}
                onActivate={onActivate}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** @deprecated use TocNestedList */
export const TocAnchor = TocNestedList
